/**
 * useEarnings Hook
 *
 * Provides earnings data and summary for host dashboard.
 * Uses real-time Firestore snapshots.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../../../../services/firebase';
import { useAuth } from '../../../../contexts';

export interface EarningsSummary {
  totalLifetime: number;
  thisMonth: number;
  available: number;
  pending: number;
  paidOut: number;
}

export interface Earning {
  id: string;
  bookingId: string;
  listingId: string;
  listingTitle: string;
  listingImage: string;
  guestId: string;
  guestName: string;
  guestAvatar: string;
  checkIn: Date;
  checkOut: Date;
  grossAmount: number;
  platformCommission: number;
  netEarnings: number;
  status: 'pending' | 'available' | 'paid_out';
  createdAt: Date;
  availableAt?: Date;
  paidOutAt?: Date;
}

type EarningsFilter = {
  status?: 'pending' | 'available' | 'paid_out';
  listingId?: string;
  dateRange?: { start: Date; end: Date };
};

const toDate = (val: unknown): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (typeof val === 'object' && 'toDate' in val && typeof (val as { toDate: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate();
  }
  const d = new Date(val as string | number);
  return isNaN(d.getTime()) ? new Date() : d;
};

export const useEarnings = (filter?: EarningsFilter) => {
  const { user } = useAuth();
  const [allEarnings, setAllEarnings] = useState<Earning[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setAllEarnings([]);
      setIsLoading(false);
      return;
    }

    const earningsRef = collection(db, 'earnings');
    const q = query(
      earningsRef,
      where('hostId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const items: Earning[] = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            bookingId: d.bookingId || '',
            listingId: d.listingId || '',
            listingTitle: d.listingTitle || '',
            listingImage: d.listingImage || '',
            guestId: d.guestId || '',
            guestName: d.guestName || '',
            guestAvatar: d.guestAvatar || '',
            checkIn: toDate(d.checkIn),
            checkOut: toDate(d.checkOut),
            grossAmount: d.grossAmount || 0,
            platformCommission: d.platformCommission || 0,
            netEarnings: d.netEarnings || 0,
            status: d.status || 'pending',
            createdAt: toDate(d.createdAt),
            availableAt: d.availableAt ? toDate(d.availableAt) : undefined,
            paidOutAt: d.paidOutAt ? toDate(d.paidOutAt) : undefined,
          };
        });
        setAllEarnings(items);
        setIsLoading(false);
      },
      (err) => {
        console.error('Earnings snapshot error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Apply client-side filters
  const earnings = useMemo(() => {
    let filtered = [...allEarnings];

    if (filter?.status) {
      filtered = filtered.filter((e) => e.status === filter.status);
    }
    if (filter?.listingId) {
      filtered = filtered.filter((e) => e.listingId === filter.listingId);
    }
    if (filter?.dateRange) {
      filtered = filtered.filter(
        (e) =>
          e.createdAt >= filter.dateRange!.start &&
          e.createdAt <= filter.dateRange!.end
      );
    }

    return filtered;
  }, [allEarnings, filter?.status, filter?.listingId, filter?.dateRange]);

  // Compute summary from all earnings (unfiltered)
  const summary = useMemo<EarningsSummary | null>(() => {
    if (isLoading) return null;

    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);

    return {
      totalLifetime: allEarnings.reduce((sum, e) => sum + e.netEarnings, 0),
      thisMonth: allEarnings
        .filter((e) => e.createdAt >= startOfMonth)
        .reduce((sum, e) => sum + e.netEarnings, 0),
      available: allEarnings
        .filter((e) => e.status === 'available')
        .reduce((sum, e) => sum + e.netEarnings, 0),
      pending: allEarnings
        .filter((e) => e.status === 'pending')
        .reduce((sum, e) => sum + e.netEarnings, 0),
      paidOut: allEarnings
        .filter((e) => e.status === 'paid_out')
        .reduce((sum, e) => sum + e.netEarnings, 0),
    };
  }, [allEarnings, isLoading]);

  return {
    earnings,
    summary,
    isLoading,
    error,
  };
};

export default useEarnings;
